'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { useAuth } from 'src/context/auth';
import { createUserActivity } from 'src/api';
import { TOGGLE_VIEW } from 'src/api/userActivities/types';

const options = [
  { label: 'Treating', value: 'treating' },
  { label: 'Referring', value: 'sending' },
] as const;

interface ProspectModeToggleProps {
  location?: string;
  defaultMode?: 'treating' | 'sending';
}

const ProspectModeToggle: React.FC<ProspectModeToggleProps> = ({
  location = 'lists',
  defaultMode,
}): React.ReactElement => {
  const { mode, setMode, columnId, apiVolumeFilterFormat } =
    useTableFilterSelections(location);
  const { user } = useAuth();
  const hasUserChangedModeRef = useRef(false);
  const lastColumnIdRef = useRef<string | null>(null);

  // Use the current mode, or fallback to defaultMode if no column is selected yet
  const displayMode = columnId ? mode : defaultMode || 'treating';

  // Reset user interaction flag when column changes (but only for completely new base IDs)
  useEffect(() => {
    const currentBaseId = columnId ? columnId.split('_')[0] : null;
    const lastBaseId = lastColumnIdRef.current
      ? lastColumnIdRef.current.split('_')[0]
      : null;

    // Only reset flag when transitioning from null to a column (truly new)
    // or when changing to a completely different base ID
    if (currentBaseId !== lastBaseId) {
      // Reset flag only if we had no column before (null → column)
      if (!lastColumnIdRef.current) {
        hasUserChangedModeRef.current = false;
      }
      lastColumnIdRef.current = columnId;
    } else if (columnId !== lastColumnIdRef.current) {
      // Same base ID but different composite key (mode change) - don't reset the flag
      lastColumnIdRef.current = columnId;
    }
  }, [columnId]);

  // Set default mode ONLY when a NEW column is created (not when opening existing columns)
  useEffect(() => {
    if (
      defaultMode &&
      columnId &&
      defaultMode !== 'treating' &&
      !hasUserChangedModeRef.current
    ) {
      // Check if this specific columnId exists in the apiVolumeFilterFormat
      // If it doesn't exist, it's a new column that should get the default mode
      const currentColumnExists =
        apiVolumeFilterFormat && apiVolumeFilterFormat[columnId];
      const isNewColumn = !currentColumnExists;

      // Only set default mode if it's a new column AND the current mode is the default 'treating'
      // AND this is the first time this column is being configured
      if (isNewColumn && mode === 'treating') {
        setMode(defaultMode);
      }
    }
  }, [columnId, defaultMode, apiVolumeFilterFormat, mode, setMode]); // Add dependencies to avoid stale closures

  const handleModeChange = useCallback(
    async (value) => {
      hasUserChangedModeRef.current = true; // Mark that user has manually changed the mode

      setMode(value);

      try {
        await createUserActivity({
          id: user?.id,
          data: { type: TOGGLE_VIEW.DISCOVERY_REFERRAL_TOGGLE },
        });
      } catch (error) {
        console.error('Failed to log user activity:', error);
      }
    },
    [setMode, user?.id]
  );

  return (
    <div className="inline-flex flex-col gap-1 w-full">
      <div className="text-primary text-xs font-semibold leading-none mb-1">
        Source
      </div>
      <div className="relative w-full h-10 select-none">
        {/* Background pill */}
        <div className="absolute inset-0 bg-level-2 rounded-full" />
        {/* Active pill */}
        <div
          className="absolute top-0 bottom-0 w-1/2 transition-all duration-300 rounded-full bg-sky-600 shadow-xl z-10"
          style={{
            left: displayMode === 'treating' ? '0%' : '50%',
          }}
        />
        <div className="relative flex w-full h-full z-20">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`flex-1 px-4 transition-colors duration-300 rounded-full font-['Inter'] text-sm leading-tight focus:outline-none
                ${
                  displayMode === option.value
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                }
              `}
              aria-pressed={displayMode === option.value}
              onClick={() => handleModeChange(option.value)}
              tabIndex={0}
              style={{ minWidth: 0, zIndex: 30 }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProspectModeToggle;
